import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FactoryIcon from "@mui/icons-material/Factory";
import HomeIcon from "@mui/icons-material/Home";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserContext } from "../../hooks/UserContext";
import { useNotification } from "../../hooks/NotificationContext";
import { appColors, menuStyles } from "../../theme/appTheme";
import { fetchCerrarSesionOAuth } from "../../services/userService";

export const drawerWidth = 252;
export const collapsedDrawerWidth = 72;
export const appBarHeight = 64;

export type MenuItemType = {
  label: string;
  path?: string;
  requiredPermission?: string;
  requiredPermissionsAny?: string[];
  requiresConMesas?: boolean;
  subItems?: MenuItemType[];
};

type Props = {
  Titulo: string;
  MenuItems: MenuItemType[] | [];
  onMenuItemClick: (path: string) => void;
  onWidthChange?: (width: number) => void;
};

const getItemIcon = (label: string) => {
  const normalized = label.toLowerCase();
  if (normalized.includes("ia")) return <AutoAwesomeIcon />;
  if (normalized.includes("producto")) return <Inventory2Icon />;
  if (normalized.includes("produccion")) return <FactoryIcon />;
  if (normalized.includes("reporte")) return <AssessmentIcon />;
  if (normalized.includes("venta")) return <PointOfSaleIcon />;
  if (normalized.includes("pedido")) return <ShoppingCartIcon />;
  if (normalized.includes("presupuesto")) return <ReceiptLongIcon />;
  if (normalized.includes("comanda")) return <RestaurantMenuIcon />;
  if (normalized.includes("autorizar")) return <AccountBalanceWalletIcon />;
  return <StorefrontIcon />;
};

const ResponsiveMenu: React.FC<Props> = ({ Titulo, MenuItems, onMenuItemClick, onWidthChange }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const { getUser, setUser } = useUserContext();
  const { notifications, unreadCount, markNotificationRead } = useNotification();
  const user = getUser();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [openSubItems, setOpenSubItems] = useState<{ [key: string]: boolean }>(() =>
    Object.fromEntries(MenuItems.filter((item) => item.subItems?.length).map((item) => [item.label, true]))
  );

  const currentWidth = collapsed ? collapsedDrawerWidth : drawerWidth;

  useEffect(() => {
    onWidthChange?.(isDesktop ? currentWidth : 0);
  }, [currentWidth, isDesktop, onWidthChange]);

  const initials = useMemo(() => {
    const source = user?.username ?? "AD";
    return String(source).split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "AD";
  }, [user]);

  const handleLogout = async () => {
    try {
      await fetchCerrarSesionOAuth(getUser()?.token);
    } catch {
      // La sesión local debe cerrarse aunque no responda el servidor OAuth.
    }

    setUser(undefined);
    setProfileAnchor(null);
    setNotificationAnchor(null);
    setMobileOpen(false);
    navigate("/login", { replace: true });
  };

  const handleNavigate = (path?: string) => {
    if (!path) return;
    onMenuItemClick(path);
    setMobileOpen(false);
  };

  const isActive = (item: MenuItemType) => {
    if (item.path && location.pathname === `/${item.path}`) return true;
    return item.subItems?.some((subItem) => subItem.path && location.pathname === `/${subItem.path}`) ?? false;
  };

  const handleNotificationClick = async (idNotificacion: number, idReferencia?: number) => {
    await markNotificationRead(idNotificacion);
    setNotificationAnchor(null);
    if (idReferencia) {
      navigate(`/pedidoweb?idPedido=${idReferencia}`);
    }
  };

  const toggleSubItems = (label: string) => {
    if (collapsed) {
      setCollapsed(false);
      setOpenSubItems((current) => ({ ...current, [label]: true }));
      return;
    }

    setOpenSubItems((current) => ({ ...current, [label]: !current[label] }));
  };

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: appColors.white }}>
      <Toolbar
        sx={{
          height: appBarHeight,
          minHeight: `${appBarHeight}px !important`,
          px: collapsed ? 1.5 : 2,
          gap: 1.5,
          borderBottom: "1px solid",
          borderColor: appColors.brandLine,
          background: appColors.white,
          position: "relative",
          "&:before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            bgcolor: appColors.brandOrange,
          },
        }}
      >
        <Avatar
          variant="rounded"
          src="/logo192.png"
          alt="Grovia"
          sx={{
            width: 40,
            height: 40,
            bgcolor: appColors.white,
            borderRadius: 2,
            boxShadow: "0 8px 18px rgba(248, 154, 49, 0.18)",
            border: "1px solid",
            borderColor: appColors.brandLine,
          }}
        >
          G
        </Avatar>
        {!collapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={800} color={appColors.charcoal} noWrap>
              Grovia
            </Typography>
            {/* <Typography variant="caption" color={appColors.textMuted} noWrap>
              Gestion Comercial
            </Typography> */}
          </Box>
        )}
      </Toolbar>

      <List
        sx={{
          px: 1.25,
          py: 1.5,
          flex: 1,
          overflowY: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: `${appColors.brandLine} transparent`,
        }}
      >
        <Tooltip title={collapsed ? "Inicio" : ""} placement="right">
          <ListItemButton
            selected={location.pathname === "/mainpage"}
            onClick={() => handleNavigate("mainpage")}
            sx={{ ...menuStyles.item, justifyContent: collapsed ? "center" : "initial" }}
          >
            <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, color: "inherit" }}>
              <HomeIcon />
            </ListItemIcon>
            {!collapsed && <ListItemText primary="Inicio" primaryTypographyProps={{ fontSize: 14 }} />}
          </ListItemButton>
        </Tooltip>

        {MenuItems.map((item) => {
          const itemActive = isActive(item);
          const hasSubItems = Boolean(item.subItems?.length);
          return (
            <React.Fragment key={item.label}>
              <Tooltip title={collapsed ? item.label : ""} placement="right">
                <ListItemButton
                  selected={itemActive}
                  onClick={() => hasSubItems ? toggleSubItems(item.label) : handleNavigate(item.path)}
                  sx={{ ...menuStyles.item, justifyContent: collapsed ? "center" : "initial" }}
                >
                  <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, color: "inherit" }}>
                    {getItemIcon(item.label)}
                  </ListItemIcon>
                  {!collapsed && <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: itemActive ? 700 : 500 }} />}
                  {!collapsed && hasSubItems && (openSubItems[item.label] ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                </ListItemButton>
              </Tooltip>

              {!collapsed && hasSubItems && (
                <Collapse in={openSubItems[item.label]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ ml: 1.25, my: 0.5, borderLeft: "1px solid", borderColor: appColors.brandLine }}>
                    {item.subItems?.map((subItem) => {
                      const subActive = Boolean(subItem.path && location.pathname === `/${subItem.path}`);
                      return (
                        <ListItemButton
                          key={subItem.label}
                          selected={subActive}
                          onClick={() => handleNavigate(subItem.path)}
                          sx={menuStyles.subItem}
                        >
                          <ListItemText
                            primary={subItem.label}
                            primaryTypographyProps={{ fontSize: 13, fontWeight: subActive ? 700 : 500 }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      {isDesktop && (
        <>
          <Divider sx={{ borderColor: appColors.brandLine }} />
          <Box sx={{ p: 1, bgcolor: appColors.white }}>
            <Tooltip title={collapsed ? "Expandir menu" : "Colapsar menu"} placement="right">
              <ListItemButton onClick={() => setCollapsed((current) => !current)} sx={{ ...menuStyles.item, minHeight: 42, justifyContent: collapsed ? "center" : "initial" }}>
                <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, color: "inherit" }}>
                  {collapsed ? <MenuIcon /> : <ChevronLeftIcon />}
                </ListItemIcon>
                {!collapsed && <ListItemText primary="Colapsar" primaryTypographyProps={{ fontSize: 14 }} />}
              </ListItemButton>
            </Tooltip>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          height: appBarHeight,
          borderBottom: "1px solid",
          borderColor: "divider",
          ml: { md: `${currentWidth}px` },
          width: { md: `calc(100% - ${currentWidth}px)` },
          bgcolor: appColors.white,
          transition: theme.transitions.create(["margin-left", "width"], { duration: theme.transitions.duration.shorter }),
        }}
      >
        <Toolbar sx={{ minHeight: `${appBarHeight}px !important`, gap: 1.5 }}>
          {!isDesktop && (
            <IconButton edge="start" onClick={() => setMobileOpen(true)} aria-label="Abrir menu">
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="subtitle2" color="text.secondary" noWrap>
            {Titulo}
          </Typography>
          <Box
            component="img"
            src="/logo192.png"
            alt="Grovia"
            sx={{ width: 30, height: 30, display: { xs: "none", sm: "block" } }}
          />
          <Box sx={{ flex: 1 }} />
          {!user?.idCliente && (
            <>
              <IconButton onClick={(event) => setNotificationAnchor(event.currentTarget)} aria-label="Notificaciones">
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              <Menu anchorEl={notificationAnchor} open={Boolean(notificationAnchor)} onClose={() => setNotificationAnchor(null)}>
                {notifications.length === 0 ? (
                  <MenuItem disabled>Sin notificaciones pendientes</MenuItem>
                ) : notifications.map((item) => (
                  <MenuItem key={item.idNotificacion} onClick={() => handleNotificationClick(item.idNotificacion, item.idReferencia)}>
                    <Box sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>{item.titulo}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }} noWrap>{item.mensaje}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          <IconButton onClick={(event) => setProfileAnchor(event.currentTarget)} aria-label="Menu de usuario">
            <Avatar sx={{ width: 34, height: 34, bgcolor: "primary.main", fontSize: 14 }}>{initials}</Avatar>
          </IconButton>
          <Typography variant="body2" sx={{ display: { xs: "none", sm: "block" } }} noWrap>
            {user?.username ?? "Admin"}
          </Typography>
          <Menu anchorEl={profileAnchor} open={Boolean(profileAnchor)} onClose={() => setProfileAnchor(null)}>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Cerrar sesion
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isDesktop ? "permanent" : "temporary"}
        open={isDesktop || mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: { md: currentWidth },
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: isDesktop ? currentWidth : "min(88vw, 320px)",
            top: 0,
            bottom: 0,
            height: "100dvh",
            maxHeight: "100dvh",
            paddingBottom: "env(safe-area-inset-bottom)",
            "@supports not (height: 100dvh)": {
              height: "100vh",
              maxHeight: "100vh",
            },
            boxSizing: "border-box",
            borderRight: "1px solid",
            borderColor: appColors.brandLine,
            boxShadow: isDesktop ? "8px 0 22px rgba(59, 55, 52, 0.05)" : undefined,
            transition: theme.transitions.create("width", { duration: theme.transitions.duration.shorter }),
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default ResponsiveMenu;
